/**
 * Native bridge — only runs when the app is hosted inside the Capacitor
 * iOS/Android shell. On the web build it's a complete no-op.
 *
 * Why we import dynamically: we don't want @capacitor/* JS to bloat the
 * production web bundle, and the modules expose a `Capacitor` global only
 * inside the WebView.
 */

let initialized = false;

function isNative() {
  // Capacitor adds this global on iOS/Android only
  return typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
}

export async function initNative() {
  if (initialized || !isNative()) return;
  initialized = true;

  try {
    const [{ StatusBar, Style }, { SplashScreen }, { Keyboard }, { App }] = await Promise.all([
      import("@capacitor/status-bar"),
      import("@capacitor/splash-screen"),
      import("@capacitor/keyboard"),
      import("@capacitor/app"),
    ]);

    // Status bar: dark navy with light text
    try {
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: "#0B1F3A" });
      await StatusBar.setOverlaysWebView({ overlay: false });
    } catch { /* ignore */ }

    // Hide native splash once UI is mounted
    try { await SplashScreen.hide({ fadeOutDuration: 250 }); } catch { /* ignore */ }

    // Resize content when keyboard shows on iOS
    try {
      Keyboard.setAccessoryBarVisible({ isVisible: false });
    } catch { /* ignore */ }

    // Hardware back button on Android — let the router handle history
    try {
      App.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) window.history.back();
        else App.exitApp();
      });
    } catch { /* ignore */ }
  } catch (e) {
    // Plugins not bundled (web build) → silently ignore
    // eslint-disable-next-line no-console
    console.debug("Capacitor plugins not available:", e?.message);
  }
}

export function isRunningNative() {
  return isNative();
}
